import { Component, input, inject, signal, computed } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { AlertStatSummaryResponse, ticketTrendsResponse, QueryType } from 'src/app/core/services/analytics.service';
import { environment } from 'src/environments/environment';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { dataIcon, warningTriangleIcon, infoSolidIcon, infoCircleIcon } from '@progress/kendo-svg-icons';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { AlertDataDialogComponent } from 'src/app/core/components/alert-data-dialog';
import { TicketDataDialogComponent } from 'src/app/core/components/ticket-data-dialog';
import { AlertDataDialogConfig, TicketDataDialogConfig, ContextMenuEvent } from 'src/app/core/models/dialog-config';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';

@Component({
  selector: 'app-distillation-funnel',
  templateUrl: './distillation-funnel.component.html',
  styleUrls: ['./distillation-funnel.component.scss'],
  standalone: true,
  imports: [CommonModule, IconsModule, AlertDataDialogComponent, TicketDataDialogComponent, SkeletonModule, TooltipModule, NotificationModule],
})
export class DistillationFunnelComponent {
  // Expose Math object to the template
  Math = Math;

  // Input properties
  ticketTrends = input<ticketTrendsResponse>(new ticketTrendsResponse());
  AlertStatSummary = input<AlertStatSummaryResponse>(new AlertStatSummaryResponse());
  organizationId = input<number | null>();
  startDate = input<string | null>();
  endDate = input<string | null>();
  queryType = input<QueryType>(QueryType.All);
  selectedOrgIds = input<number[]>([]);

  /** Optional array of column field names to display in alert dialogs. If not provided, all columns are shown. */
  alertVisibleColumns = input<string[]>();

  /** Optional array of column field names to display in ticket dialogs. If not provided, all columns are shown. */
  ticketVisibleColumns = input<string[]>();

  /** Optional security tool filter - comma-separated list of product IDs */
  securityTool = input<string>();

  /** Whether the alert/ticket dialogs opened from the funnel show the Export to Excel button. Defaults to true. */
  showExcelExport = input<boolean>(true);

  /**
   * Whether the Download CSV button is offered on the funnel's alert/ticket
   * dialogs (opt-in per context; off by default so the shared funnel is
   * unchanged elsewhere).
   */
  enableCsvExport = input(false);

  /** Loading state for skeleton display */
  isLoading = input<boolean>(false);

  // Dialog state management with signals
  public showAlertDialog = signal(false);
  public showTicketDialog = signal(false);
  public alertDialogConfig = signal<AlertDataDialogConfig | null>(null);
  public ticketDialogConfig = signal<TicketDataDialogConfig | null>(null);

  /** Injected services for server-side CSV export via report-job pipeline */
  private http = inject(HttpClient);
  private reportJobService = inject(ReportJobService);
  private notificationService = inject(NotificationService);

  /** Whether a CSV export is currently being queued */
  public isExporting = signal(false);

  /** Report type key of the currently open dialog (set per funnel stage). */
  private currentReportKey = signal<string | null>(null);

  /** SOC report type key per funnel stage (alert and ticket stages). */
  private readonly reportKeyByStage: Record<string, string> = {
    raw_events: 'total-ingested-events-soc-report',
    detections: 'consolidated-cases-soc-report',
    escalations: 'escalations-soc-report',
    incidents: 'incidents-soc-report',
  };

  // SVG Icons
  public dataIcon: SVGIcon = dataIcon;
  public warningTriangleIcon: SVGIcon = warningTriangleIcon;
  public infoSolidIcon: SVGIcon = infoSolidIcon;
  public infoCircleIcon: SVGIcon = infoCircleIcon;

  /**
   * Handles context menu selection for viewing data
   */
  onContextMenuSelect(event: ContextMenuEvent, stage: string): void {
    if (!event?.text) {
      console.warn('Invalid event structure:', event);
      return;
    }

    if (event.text === 'View Alert Data') {
      this.openAlertDialog(stage);
    } else if (event.text === 'View Ticket Data') {
      this.openTicketDialog(stage);
    }
  }

  /**
   * Opens the alert data dialog with configuration
   * Example: Hides customer and parent account columns for cleaner internal reporting
   */
  private openAlertDialog(stage: string): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const reportKey = this.enableCsvExport() ? (this.reportKeyByStage[stage] ?? null) : null;
    this.currentReportKey.set(reportKey);
    const config: AlertDataDialogConfig = {
      organizationId: this.organizationId() ?? null,
      startDate: this.startDate() ?? null,
      endDate: this.endDate() ?? null,
      queryType: this.queryType(),
      selectedOrgIds: this.selectedOrgIds(),
      event: stage,
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`,
      visibleColumns: this.alertVisibleColumns(),
      securityTool: this.securityTool(),
      showExcelExport: this.showExcelExport(),
      reportTypeKey: reportKey ?? undefined,
    };

    this.alertDialogConfig.set(config);
    this.showAlertDialog.set(true);
  }

  /**
   * Opens the ticket data dialog with configuration
   */
  private openTicketDialog(stage: string): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const reportKey = this.enableCsvExport() ? (this.reportKeyByStage[stage] ?? null) : null;
    this.currentReportKey.set(reportKey);
    const config: TicketDataDialogConfig = {
      organizationId: this.organizationId() ?? null,
      startDate: this.startDate() ?? null,
      endDate: this.endDate() ?? null,
      queryType: this.queryType(),
      selectedOrgIds: this.selectedOrgIds(),
      event: stage,
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/ticketeventsdetails/`,
      useDifferentDateField: false, // Use created_time by default
      visibleColumns: this.ticketVisibleColumns(),
      securityTool: this.securityTool(),
      showExcelExport: this.showExcelExport(),
      reportTypeKey: reportKey ?? undefined,
      additionalParams: { $filter: "department_name eq 'SOC Incidents' and ticket_status ne null" },
    };

    this.ticketDialogConfig.set(config);
    this.showTicketDialog.set(true);
  }

  /**
   * Queue a server-side CSV export for the currently open funnel stage (alert
   * or ticket). Resolves the report template on demand based on the stage's
   * report type key.
   */
  triggerExport(): void {
    const reportKey = this.currentReportKey();
    const orgId = this.organizationId();
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!reportKey || !orgId || !startDate || !endDate) {
      this.notificationService.show({
        content: 'Unable to export: missing organization or date range.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        hideAfter: 5000,
      });
      return;
    }

    this.isExporting.set(true);

    const url = `${environment.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${reportKey}'`;
    this.http
      .get<{ value: any[] }>(url)
      .pipe(map((response) => response.value?.[0]?.report_template?.id ?? null))
      .subscribe({
        next: (templateId) => {
          if (!templateId) {
            this.isExporting.set(false);
            this.notificationService.show({
              content: 'CSV export is not available for this view yet.',
              type: { style: 'warning', icon: true },
              position: { horizontal: 'right', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              hideAfter: 5000,
            });
            return;
          }

          const config: ReportJobConfig = {
            organizations: [orgId],
            primary_organization_id: orgId,
            report_template_id: templateId,
            start_date: formatDate(startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
            end_date: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
            delivery_method: 'download',
            report_params: {
              filter_type: this.queryType(),
              ...(this.securityTool() ? { security_tool: this.securityTool()! } : {}),
            },
          };

          this.reportJobService.createReportJob(config).subscribe({
            next: () => {
              this.isExporting.set(false);
              this.notificationService.show({
                content: 'Export queued. You will find the CSV in the Reports history once generated.',
                type: { style: 'success', icon: true },
                position: { horizontal: 'right', vertical: 'top' },
                animation: { type: 'fade', duration: 400 },
                closable: true,
                hideAfter: 5000,
              });
            },
            error: () => {
              this.isExporting.set(false);
              this.notificationService.show({
                content: 'Failed to queue export. Please try again.',
                type: { style: 'error', icon: true },
                position: { horizontal: 'right', vertical: 'top' },
                animation: { type: 'fade', duration: 400 },
                hideAfter: 5000,
              });
            },
          });
        },
        error: () => {
          this.isExporting.set(false);
          this.notificationService.show({
            content: 'Failed to queue export. Please try again.',
            type: { style: 'error', icon: true },
            position: { horizontal: 'right', vertical: 'top' },
            animation: { type: 'fade', duration: 400 },
            hideAfter: 5000,
          });
        },
      });
  }

  /**
   * Handles alert dialog close
   */
  onAlertDialogClose(): void {
    this.showAlertDialog.set(false);
    this.alertDialogConfig.set(null);
  }

  /**
   * Handles ticket dialog close
   */
  onTicketDialogClose(): void {
    this.showTicketDialog.set(false);
    this.ticketDialogConfig.set(null);
  }
}
